import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  freeTrialPopupPlacement: z.coerce
    .number({
      required_error: 'Required (use 0 or negative to disable)',
      invalid_type_error: 'Must be a number',
    })
    .int()
    .describe(
      'Number of remaining free trial notes (excluding sample notes) when popup should appear (0 or negative = disabled)',
    ),
});

export const FreeTrialPopupPlacement = () => {
  const [updateConfig, { isLoading: isSaving }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const {
    remoteAppConfig: { freeTrialPopupPlacement },
  } = useGetAppConfig();

  const { onSettled } = useOnSettled();

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(schema as any),
    defaultValues: {
      freeTrialPopupPlacement: freeTrialPopupPlacement ?? -1,
    },
  });

  useEffect(() => {
    setValue(
      'freeTrialPopupPlacement',
      freeTrialPopupPlacement ?? -1,
    );
  }, [freeTrialPopupPlacement, setValue]);

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">
          Free Trial Start Popup Placement
        </Typography>
        <Tooltip title="Number of remaining free trial notes (excluding sample notes) when popup should appear. Use 0 or negative to disable. Example: If free notes = 5, set to 4 to show after first real note.">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <TextField
            label="Popup Placement (remaining notes threshold)"
            disabled={isSaving}
            error={!!formState.errors.freeTrialPopupPlacement}
            type="number"
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('freeTrialPopupPlacement', {
              required: true,
            })}
          />
          {(formState.isDirty || formState.isSubmitting) && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving || !formState.isValid}
              endIcon={isSaving && <CircularProgress size={14} />}
            >
              {isSaving ? 'Updating' : 'Update'} Free Trial Start Popup
              Placement
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};

